import { useEffect } from 'react'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { AppLayout } from '@/components/layout'
import { AuthProvider, ProtectedRoute, GuestRoute, PermissionProtectedRoute } from '@/components/auth'
import { ErrorBoundary } from '@/components/error-boundary'
import { AdminLoginPage } from '@/features/auth/admin-login'
import { DashboardPage } from '@/features/dashboard/dashboard-page'
import { MembersPage } from '@/features/members/members-page'
import { BanksPage } from '@/features/banks/banks-page'
import { SlipsPage } from '@/features/slips/slips-page'
import { PromotionsPage } from '@/features/promotions'
import { TransactionsPendingPage } from '@/features/transactions/transactions-pending-page'
import { TransactionsHistoryPage } from '@/features/transactions/transactions-history-page'
import { SmartCashierPage } from '@/features/transactions/smart-cashier-page'
import { AuditLogPage } from '@/features/audit/audit-log-page'
import { FinancialReportPage } from '@/features/reports/financial-report-page'
import { MonthlyReportPage } from '@/features/reports/monthly-report-page'
import { MembersReportPage } from '@/features/reports/members-report-page'
import { TransactionsReportPage } from '@/features/reports/transactions-report-page'
import { BanksReportPage } from '@/features/reports/banks-report-page'
import { AffiliatesListPage } from '@/features/affiliates/affiliates-list-page'
import { CommissionsPage } from '@/features/affiliates/commissions-page'
import { ReferralLinksPage } from '@/features/affiliates/referral-links-page'
import { PayoutsPage } from '@/features/affiliates/payouts-page'
import { AffiliateReportPage } from '@/features/affiliates/affiliate-report-page'
import { GamesListPage } from '@/features/games/games-list-page'
import { ProvidersPage } from '@/features/games/providers-page'
import { CategoriesPage } from '@/features/games/categories-page'
import { GameSettingsPage } from '@/features/games/game-settings-page'
import { GameReportPage } from '@/features/games/game-report-page'
import { SettingsPage } from '@/features/settings/settings-page'
import { CronJobsPage } from '@/features/cron-jobs/cron-jobs-page'
import { AclPage } from '@/features/acl/acl-page'
import { AdminsPage } from '@/features/admins/admins-page'
import { ChangelogPage } from '@/features/changelog/changelog-page'
import { LossbackPage } from '@/features/lossback/lossback-page'
import { LossbackHistoryPage } from '@/features/lossback/lossback-history-page'
import { AutoLossbackPage } from '@/features/auto-lossback/auto-lossback-page'
import { MemberGroupsPage } from '@/features/member-groups/member-groups-page'
import { MarketingToolsPage } from '@/features/marketing/marketing-tools-page'
import { ProfilePage } from '@/features/profile/profile-page'
import { DevicesPage } from '@/features/devices/devices-page'
import { ParseExplorer } from '@/features/dev-tools/parse-explorer'
import { FieldMappingViewer } from '@/features/dev-tools/field-mapping-viewer'
import { UserExplorer } from '@/features/dev-tools/user-explorer'
import {
  AffiliateLogin,
  AffiliateLayout,
  AffiliateDashboard,
  AffiliateLinks,
  AffiliateReferrals,
  AffiliateCommissions,
  AffiliateWithdraw,
  AffiliateReports,
  AffiliateSettings,
  TeamOverview,
  TeamManagement,
  TeamReports,
} from '@/features/affiliate-portal'
import { useThemeStore } from '@/stores/theme-store'
import { AIAssistant } from '@/components/ai-assistant/ai-assistant'
import { useAuth } from '@/components/auth'

// ============================================
// Protected Layout with Auth Check
// ============================================

function ProtectedAppLayout() {
  return (
    <ProtectedRoute>
      <AppLayout />
    </ProtectedRoute>
  );
}

// ============================================
// App Routes
// ============================================

function AppRoutes() {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  // ✅ Global session expired handler — ฟังจาก parse-client.ts ที่ยิง event เมื่อเจอ 209
  useEffect(() => {
    const handleSessionExpired = () => {
      console.warn('⚠️ [App] Session expired — logging out');
      logout().finally(() => navigate('/login', { replace: true }));
    };
    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, [logout, navigate]);

  return (
    <>
      <Routes>
        {/* ============================================ */}
        {/* Public Routes (Guest Only) */}
        {/* ============================================ */}
        <Route 
          path="/login" 
          element={
            <GuestRoute>
              <AdminLoginPage />
            </GuestRoute>
          } 
        />
        <Route 
          path="/auth/login" 
          element={
            <GuestRoute>
              <AdminLoginPage />
            </GuestRoute>
          } 
        />
        
        {/* ============================================ */}
        {/* Protected Routes (Login Required) */}
        {/* ============================================ */}
        <Route element={<ProtectedAppLayout />}>
          {/* Dashboard */}
          <Route 
            path="/dashboard" 
            element={
              <PermissionProtectedRoute requiredPath="/dashboard">
                <DashboardPage />
              </PermissionProtectedRoute>
            } 
          />
          
          {/* Members & Banks */}
          <Route 
            path="/members" 
            element={
              <PermissionProtectedRoute requiredPath="/members">
                <MembersPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/banks" 
            element={
              <PermissionProtectedRoute requiredPath="/banks">
                <BanksPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/promotions" 
            element={
              <PermissionProtectedRoute requiredPath="/promotions">
                <PromotionsPage />
              </PermissionProtectedRoute>
            } 
          />
          
          {/* Transactions */}
          <Route 
            path="/transactions" 
            element={
              <PermissionProtectedRoute requiredPath="/transactions">
                <TransactionsPendingPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/transactions/pending" 
            element={
              <PermissionProtectedRoute requiredPath="/transactions">
                <TransactionsPendingPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/transactions/history" 
            element={
              <PermissionProtectedRoute requiredPath="/transactions">
                <TransactionsHistoryPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/transactions/smart-cashier" 
            element={
              <PermissionProtectedRoute requiredPath="/transactions/smart-cashier">
                <SmartCashierPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/transactions/slips" 
            element={
              <PermissionProtectedRoute requiredPath="/transactions/slips">
                <SlipsPage />
              </PermissionProtectedRoute>
            } 
          />
          
          {/* Audit */}
          <Route 
            path="/audit" 
            element={
              <PermissionProtectedRoute requiredPath="/audit-log">
                <AuditLogPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/audit-log" 
            element={
              <PermissionProtectedRoute requiredPath="/audit-log">
                <AuditLogPage />
              </PermissionProtectedRoute>
            } 
          />
          
          {/* Devices */}
          <Route 
            path="/devices" 
            element={
              <PermissionProtectedRoute requiredPath="/devices">
                <DevicesPage />
              </PermissionProtectedRoute>
            } 
          />
          
          {/* Reports */}
          <Route 
            path="/reports/financial" 
            element={
              <PermissionProtectedRoute requiredPath="/reports/financial">
                <FinancialReportPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/reports/monthly" 
            element={
              <PermissionProtectedRoute requiredPath="/reports/monthly">
                <MonthlyReportPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/reports/members" 
            element={
              <PermissionProtectedRoute requiredPath="/reports/members">
                <MembersReportPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/reports/transactions" 
            element={
              <PermissionProtectedRoute requiredPath="/reports/transactions">
                <TransactionsReportPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/reports/banks" 
            element={
              <PermissionProtectedRoute requiredPath="/reports/banks">
                <BanksReportPage />
              </PermissionProtectedRoute>
            } 
          />
          
          {/* Games */}
          <Route 
            path="/games/list" 
            element={
              <PermissionProtectedRoute requiredPath="/games/list">
                <GamesListPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/games/providers" 
            element={
              <PermissionProtectedRoute requiredPath="/games/providers">
                <ProvidersPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/games/categories" 
            element={
              <PermissionProtectedRoute requiredPath="/games/categories">
                <CategoriesPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/games/settings" 
            element={
              <PermissionProtectedRoute requiredPath="/games/settings">
                <GameSettingsPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/games/report" 
            element={
              <PermissionProtectedRoute requiredPath="/games/report">
                <GameReportPage />
              </PermissionProtectedRoute>
            } 
          />
          
          {/* Affiliates */}
          <Route 
            path="/affiliates/list" 
            element={
              <PermissionProtectedRoute requiredPath="/affiliates/list">
                <AffiliatesListPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/affiliates/commissions" 
            element={
              <PermissionProtectedRoute requiredPath="/affiliates/commissions">
                <CommissionsPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/affiliates/links" 
            element={
              <PermissionProtectedRoute requiredPath="/affiliates/links">
                <ReferralLinksPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/affiliates/payouts" 
            element={
              <PermissionProtectedRoute requiredPath="/affiliates/payouts">
                <PayoutsPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/affiliates/report" 
            element={
              <PermissionProtectedRoute requiredPath="/affiliates/report">
                <AffiliateReportPage />
              </PermissionProtectedRoute>
            } 
          />
          
          {/* Marketing Tools */}
          <Route 
            path="/marketing" 
            element={
              <PermissionProtectedRoute requiredPath="/marketing">
                <MarketingToolsPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/marketing/banners" 
            element={
              <PermissionProtectedRoute requiredPath="/marketing/banners">
                <MarketingToolsPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/marketing/campaigns" 
            element={
              <PermissionProtectedRoute requiredPath="/marketing/campaigns">
                <MarketingToolsPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/marketing/popups" 
            element={
              <PermissionProtectedRoute requiredPath="/marketing/popups">
                <MarketingToolsPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/marketing/sms" 
            element={
              <PermissionProtectedRoute requiredPath="/marketing/sms">
                <MarketingToolsPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/marketing/line" 
            element={
              <PermissionProtectedRoute requiredPath="/marketing/line">
                <MarketingToolsPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/marketing/email" 
            element={
              <PermissionProtectedRoute requiredPath="/marketing/email">
                <MarketingToolsPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/marketing/push" 
            element={
              <PermissionProtectedRoute requiredPath="/marketing/push">
                <MarketingToolsPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/marketing/landing" 
            element={
              <PermissionProtectedRoute requiredPath="/marketing/landing">
                <MarketingToolsPage />
              </PermissionProtectedRoute>
            } 
          />
          
          {/* Cron Jobs */}
          <Route 
            path="/cron-jobs" 
            element={
              <PermissionProtectedRoute requiredPath="/cron-jobs">
                <CronJobsPage />
              </PermissionProtectedRoute>
            } 
          />
          
          {/* Lossback */}
          <Route 
            path="/lossback/manage" 
            element={
              <PermissionProtectedRoute requiredPath="/lossback/manage">
                <LossbackPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/lossback/auto" 
            element={
              <PermissionProtectedRoute requiredPath="/lossback/auto">
                <AutoLossbackPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/lossback/records" 
            element={
              <PermissionProtectedRoute requiredPath="/lossback/records">
                <LossbackHistoryPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route path="/lossback" element={<Navigate to="/lossback/manage" replace />} />
          
          {/* Member Groups */}
          <Route 
            path="/member-groups" 
            element={
              <PermissionProtectedRoute requiredPath="/member-groups">
                <MemberGroupsPage />
              </PermissionProtectedRoute>
            } 
          />
          
          {/* Admins & ACL */}
          <Route 
            path="/admins" 
            element={
              <PermissionProtectedRoute requiredPath="/admins">
                <AdminsPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/acl" 
            element={
              <PermissionProtectedRoute requiredPath="/acl">
                <AclPage />
              </PermissionProtectedRoute>
            } 
          />
          
          {/* Changelog */}
          <Route 
            path="/changelog" 
            element={
              <PermissionProtectedRoute requiredPath="/changelog">
                <ChangelogPage />
              </PermissionProtectedRoute>
            } 
          />
          
          {/* Settings & Profile */}
          <Route 
            path="/settings" 
            element={
              <PermissionProtectedRoute requiredPath="/settings">
                <SettingsPage />
              </PermissionProtectedRoute>
            } 
          />
          <Route path="/profile" element={<ProfilePage />} />
          
          {/* Dev Tools (Protected - hidden in production) */}
          <Route 
            path="/dev/parse-explorer" 
            element={
              <PermissionProtectedRoute requiredPath="/dev-tools">
                <ParseExplorer />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/dev/field-mapping" 
            element={
              <PermissionProtectedRoute requiredPath="/dev-tools">
                <FieldMappingViewer />
              </PermissionProtectedRoute>
            } 
          />
          <Route 
            path="/dev/users" 
            element={
              <PermissionProtectedRoute requiredPath="/dev-tools">
                <UserExplorer />
              </PermissionProtectedRoute>
            } 
          />
          
          {/* Root redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Route>

        {/* ============================================ */}
        {/* Affiliate Portal Routes */}
        {/* ============================================ */}
        <Route path="/affiliate/login" element={<AffiliateLogin />} />
        <Route path="/affiliate" element={<AffiliateLayout />}>
          <Route index element={<AffiliateDashboard />} />
          <Route path="links" element={<AffiliateLinks />} />
          <Route path="referrals" element={<AffiliateReferrals />} />
          <Route path="commissions" element={<AffiliateCommissions />} />
          <Route path="withdraw" element={<AffiliateWithdraw />} />
          <Route path="reports" element={<AffiliateReports />} />
          <Route path="settings" element={<AffiliateSettings />} />
          {/* Leader Pages */}
          <Route path="team" element={<TeamOverview />} />
          <Route path="team/manage" element={<TeamManagement />} />
          <Route path="team/reports" element={<TeamReports />} />
        </Route>

        {/* ============================================ */}
        {/* Fallback - Redirect to login or dashboard */}
        {/* ============================================ */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      
      {/* AI Assistant (only show when authenticated and enabled) */}
      {isAuthenticated && (
        <ErrorBoundary fallback={null}>
          <AIAssistant />
        </ErrorBoundary>
      )}
    </>
  );
}

// ============================================
// Main App
// ============================================

function App() {
  const { mode, colorTheme } = useThemeStore()

  useEffect(() => {
    // Apply theme on mount
    const root = document.documentElement
    
    // Apply mode
    if (mode === 'dark') {
      root.classList.add('dark')
      root.classList.remove('light')
    } else {
      root.classList.add('light')
      root.classList.remove('dark')
    }
    
    // Apply color theme
    root.classList.remove('theme-midnight', 'theme-ocean')
    root.classList.add(`theme-${colorTheme}`)
  }, [mode, colorTheme])

  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  )
}

export default App
